import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Typography, Fade } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { COLOR_THEME_PRIMARY, COLOR_THEME_BLUE_PRIMARY } from '../../../styles/values/Colors';

const pulse = keyframes`
  0%, 100% {
    opacity: 0.4;
  }
  50% {
    opacity: 1;
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const sparkle = keyframes`
  0%, 100% {
    opacity: 0;
    transform: scale(0);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
`;

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '200px',
  padding: '40px 20px',
  gap: '20px',
  position: 'relative',
  background: `linear-gradient(-45deg, 
    rgba(25, 118, 210, 0.05), 
    rgba(66, 165, 245, 0.05), 
    rgba(25, 118, 210, 0.03), 
    rgba(66, 165, 245, 0.03))`,
  backgroundSize: '400% 400%',
  animation: `${gradientShift} 8s ease infinite`,
  borderRadius: '12px',
  overflow: 'hidden',
}));

const LoadingText = styled(Typography)(({ theme }) => ({
  color: COLOR_THEME_BLUE_PRIMARY,
  fontWeight: 600,
  animation: `${pulse} 2s infinite`,
  textAlign: 'center',
  letterSpacing: '0.5px',
}));

const QuoteText = styled(Typography)(({ theme }) => ({
  color: COLOR_THEME_PRIMARY,
  fontStyle: 'italic',
  textAlign: 'center',
  maxWidth: '400px',
  lineHeight: 1.6,
  opacity: 0.8,
  animation: `${float} 3s ease-in-out infinite`,
  fontSize: '0.9rem',
  fontWeight: 400,
}));

const StyledCircularProgress = styled(CircularProgress)(({ theme }) => ({
  color: COLOR_THEME_PRIMARY,
  animation: `${float} 2s ease-in-out infinite`,
  '& .MuiCircularProgress-circle': {
    strokeLinecap: 'round',
  },
}));

const ParticleContainer = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  pointerEvents: 'none',
  overflow: 'hidden',
});

const Particle = styled(Box)<{ delay: number; size: number; left: number; duration: number }>(
  ({ delay, size, left, duration }) => ({
    position: 'absolute',
    width: `${size}px`,
    height: `${size}px`,
    backgroundColor: COLOR_THEME_BLUE_PRIMARY,
    borderRadius: '50%',
    opacity: 0.3,
    left: `${left}%`,
    bottom: '-10px',
    animation: `${sparkle} ${duration}s ease-in-out infinite`,
    animationDelay: `${delay}s`,
  })
);

const inspirationalQuotes = [
  "Great things take time to build...",
  "Every moment of loading brings you closer to excellence...",
  "Quality is worth the wait...",
  "Preparing something amazing for you...",
  "Good things come to those who wait...",
  "Building the future, one moment at a time...",
  "Excellence is in progress...",
  "Creating magic behind the scenes...",
  "Your patience makes the experience better...",
  "Crafting perfection takes a moment...",
  "Loading happiness and productivity...",
  "Preparing your digital workspace..."
];

interface LazyLoadingSuspenseProps {
  children: React.ReactNode;
  fallbackText?: string;
  minHeight?: string;
}

const LazyLoadingFallback: React.FC<{ text?: string; minHeight?: string }> = ({ 
  text = 'Loading...', 
  minHeight = '200px' 
}) => {
  const [currentQuote, setCurrentQuote] = useState(0);
  const [particles] = useState(() => 
    Array.from({ length: 8 }, (_, i) => ({
      id: i,
      delay: i * 0.5,
      size: Math.random() * 4 + 2,
      left: Math.random() * 100,
      duration: Math.random() * 3 + 2,
    }))
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentQuote((prev) => (prev + 1) % inspirationalQuotes.length);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  return (
    <Fade in timeout={500}>
      <LoadingContainer sx={{ minHeight }}>
        <ParticleContainer>
          {particles.map((particle) => (
            <Particle
              key={particle.id}
              delay={particle.delay}
              size={particle.size}
              left={particle.left}
              duration={particle.duration}
            />
          ))}
        </ParticleContainer>
        
        <StyledCircularProgress size={50} thickness={3} />
        
        <Box sx={{ textAlign: 'center', gap: 2, display: 'flex', flexDirection: 'column' }}>
          <LoadingText variant="h6">{text}</LoadingText>
          
          <Fade 
            in 
            timeout={600} 
            key={currentQuote}
            style={{ transitionDelay: '200ms' }}
          >
            <QuoteText variant="body2">
              {inspirationalQuotes[currentQuote]}
            </QuoteText>
          </Fade>
        </Box>
      </LoadingContainer>
    </Fade>
  );
};

const LazyLoadingSuspense: React.FC<LazyLoadingSuspenseProps> = ({ 
  children, 
  fallbackText,
  minHeight 
}) => (
  <React.Suspense fallback={<LazyLoadingFallback text={fallbackText} minHeight={minHeight} />}>
    {children}
  </React.Suspense>
);

export default LazyLoadingSuspense;
export { LazyLoadingFallback }; 